import { useCallback } from 'react';
import { apiService } from '../services/api';
import {
  dispatchInfographicGeneration,
  pollInfographicStatus,
} from '../services/infographicGeneration';
import type { InfographicGenerateRequest, ImageOption } from '../types/infographic';
import { useGenerationJobs } from './useGenerationJobs';

/**
 * Job state for the async infographic generation flow. No longer block on
 * the POST response — after dispatch the job is "pending" and this hook polls
 * the status endpoint until done/error/timeout.
 *
 * State is tracked per post (see useGenerationJobs), so several posts can
 * generate at once.
 *
 * imageOptions/selectedImageIndex on 'done' are set only when the quality
 * gate exhausted every attempt — a caveat on an otherwise-done job (see
 * InfographicGenerateResponse), not a distinct status.
 */
export type InfographicGenerationState =
  | { status: 'idle' }
  | { status: 'pending'; postId: string }
  | {
      status: 'done';
      postId: string;
      imageUrl: string;
      imageOptions?: ImageOption[] | null;
      selectedImageIndex?: number | null;
    }
  | { status: 'error'; postId: string; errorMessage: string }
  | { status: 'timeout'; postId: string };

export interface InfographicGenerationCallbacks {
  onDone?: (
    postId: string,
    imageUrl: string,
    imageOptions?: ImageOption[] | null,
    selectedImageIndex?: number | null
  ) => void | Promise<void>;
  onError?: (postId: string, message: string) => void | Promise<void>;
  onTimeout?: (postId: string) => void | Promise<void>;
}

const IDLE: InfographicGenerationState = { status: 'idle' };

export function useInfographicGeneration() {
  const { jobs, setJob, stopPolling, trackPolling, clear } = useGenerationJobs<InfographicGenerationState>();

  // Polls pollPostId's job, recording its state under the editor's postId.
  const poll = useCallback(
    (postId: string, pollPostId: string, callbacks: InfographicGenerationCallbacks) => {
      const stop = pollInfographicStatus(apiService, {
        postId: pollPostId,
        onDone: async (imageUrl, imageOptions, selectedImageIndex) => {
          setJob(postId, { status: 'done', postId: pollPostId, imageUrl, imageOptions, selectedImageIndex });
          await callbacks.onDone?.(pollPostId, imageUrl, imageOptions, selectedImageIndex);
        },
        onError: async (message) => {
          setJob(postId, { status: 'error', postId: pollPostId, errorMessage: message });
          await callbacks.onError?.(pollPostId, message);
        },
        onTimeout: async () => {
          setJob(postId, { status: 'timeout', postId: pollPostId });
          await callbacks.onTimeout?.(pollPostId);
        },
      });
      trackPolling(postId, stop);
    },
    [setJob, trackPolling]
  );

  const start = useCallback(
    async (request: InfographicGenerateRequest, callbacks: InfographicGenerationCallbacks = {}) => {
      const postId = request.post_id;
      // Replaces only this post's previous job — other posts keep generating.
      stopPolling(postId);
      setJob(postId, { status: 'pending', postId });

      let dispatched;
      try {
        dispatched = await dispatchInfographicGeneration(apiService, request);
      } catch (err: any) {
        const message = err?.message || 'Could not start generation. Please try again.';
        setJob(postId, { status: 'error', postId, errorMessage: message });
        await callbacks.onError?.(postId, message);
        return;
      }

      // Guard: the post already has an image and force_regenerate wasn't set.
      // Use output_path (the existing image) instead of re-generating.
      const alreadyGenerated =
        dispatched.response &&
        'error_code' in dispatched.response &&
        dispatched.response.error_code === 'ALREADY_GENERATED';
      if (alreadyGenerated) {
        const existingUrl = (dispatched.response as import('../types/infographic').InfographicAlreadyGeneratedResponse).output_path;
        if (existingUrl) {
          setJob(postId, { status: 'done', postId, imageUrl: existingUrl });
          await callbacks.onDone?.(postId, existingUrl);
        } else {
          const message =
            (dispatched.response as import('../types/infographic').InfographicAlreadyGeneratedResponse).message ||
            'This post already has a generated image.';
          setJob(postId, { status: 'error', postId, errorMessage: message });
          await callbacks.onError?.(postId, message);
        }
        return;
      }

      poll(postId, dispatched.postId || postId, callbacks);
    },
    [poll, setJob, stopPolling]
  );

  const retry = useCallback(
    async (request: InfographicGenerateRequest, callbacks: InfographicGenerationCallbacks = {}) => {
      // Explicit user action — force a fresh generation so a still-running
      // previous job (e.g. the timed-out one) can't trip ALREADY_GENERATED.
      await start({ ...request, force_regenerate: true }, callbacks);
    },
    [start]
  );

  // Resumes polling for a job that's already running server-side (e.g. the
  // user refreshed the page mid-generation) without dispatching a new one.
  const resume = useCallback(
    (postId: string, callbacks: InfographicGenerationCallbacks = {}) => {
      stopPolling(postId);
      setJob(postId, { status: 'pending', postId });
      poll(postId, postId, callbacks);
    },
    [poll, setJob, stopPolling]
  );

  const stateFor = (postId?: string | null): InfographicGenerationState => (postId && jobs[postId]) || IDLE;

  return {
    stateFor,
    isPendingFor: (postId?: string | null) => stateFor(postId).status === 'pending',
    isErrorFor: (postId?: string | null) => stateFor(postId).status === 'error',
    isTimedOutFor: (postId?: string | null) => stateFor(postId).status === 'timeout',
    errorMessageFor: (postId?: string | null) => {
      const state = stateFor(postId);
      return state.status === 'error' ? state.errorMessage : null;
    },
    start,
    retry,
    resume,
    clear,
  };
}
